import { Stack, TextField } from "@mui/material";
import { useCallback, useState } from "react";
import type {
  ConditionDescriptor,
  ConditionFilterDescriptor,
} from "../../../../../../../../api/types/fieldConfigurationTypes.ts";
import { ConditionEqualityType } from "../../../../../../../../api/types/fieldConfigurationTypes.ts";
import type { ConditionField } from "../../../../../../../../api/types/svTypes.ts";
import { isTextOperator, MAX_CONDITION_TEXT_LENGTH } from "./conditionOperatorOptions.ts";
import ConditionOperatorSelect from "./ConditionOperatorSelect.tsx";
import TextFilterCondition from "./text-filter-grid-with-lazyloading/TextFilterCondition.tsx";

interface Props {
  field: ConditionField;
  narrowingFilters: ConditionDescriptor[];
  showOnlySelectedByDefault?: boolean;
  readonly?: boolean;
  onFilterUpdated: (filter: ConditionFilterDescriptor) => void;
}

const ValueFilterCondition = ({
  field,
  narrowingFilters,
  showOnlySelectedByDefault,
  readonly,
  onFilterUpdated,
}: Props) => {
  const [equalityType, setEqualityType] = useState<ConditionEqualityType>(
    () => field.config.filter?.equalityType ?? ConditionEqualityType.Equal
  );
  const [text, setText] = useState(() =>
    isTextOperator(field.config.filter?.equalityType) ? (field.config.filter?.values[0]?.trim() ?? "") : ""
  );
  const [selectedValues, setSelectedValues] = useState<(string | null)[]>(() =>
    isTextOperator(field.config.filter?.equalityType) ? [] : (field.config.filter?.values ?? [])
  );

  const pushFilter = useCallback(
    (values: (string | null)[], operator: ConditionEqualityType) => {
      onFilterUpdated({ dimensionName: field.meta.name, values, equalityType: operator });
    },
    [field.meta.name, onFilterUpdated]
  );

  const handleSelectedValuesChange = useCallback(
    (values: (string | null)[]) => {
      setSelectedValues(values);
      pushFilter(values, equalityType);
    },
    [equalityType, pushFilter]
  );

  const isTextValue = isTextOperator(equalityType);

  const handleOperatorChange = (operator: ConditionEqualityType) => {
    const nextIsTextValue = isTextOperator(operator);
    const modeChanged = nextIsTextValue !== isTextValue;
    setEqualityType(operator);
    if (!nextIsTextValue) {
      pushFilter(selectedValues, operator);
      return;
    }
    const nextText = modeChanged ? "" : text;
    setText(nextText);
    const term = nextText.trim();
    pushFilter(term.length > 0 ? [term] : [], operator);
  };

  const handleTextChange = (value: string) => {
    setText(value);
    const term = value.trim();
    pushFilter(term.length > 0 ? [term] : [], equalityType);
  };

  return (
    <Stack spacing={2} sx={{ p: 2, width: "100%", flex: isTextValue ? undefined : 1 }}>
      <ConditionOperatorSelect value={equalityType} disabled={readonly} onChange={handleOperatorChange} />
      {isTextValue ? (
        <TextField
          value={text}
          disabled={readonly}
          fullWidth
          slotProps={{ htmlInput: { maxLength: MAX_CONDITION_TEXT_LENGTH } }}
          onChange={(e) => handleTextChange(e.target.value)}
        />
      ) : (
        <TextFilterCondition
          field={field}
          selectedValues={selectedValues}
          readonly={readonly}
          narrowingFilters={narrowingFilters}
          showOnlySelectedByDefault={showOnlySelectedByDefault}
          onSelectedValuesChange={handleSelectedValuesChange}
        />
      )}
    </Stack>
  );
};

export default ValueFilterCondition;
